import { useCallback, useEffect, useMemo, useState } from "react";
import { useMeasurements as useMeasurementsQuery } from "../../../hooks/queries/useMeasurements";

const normalizeMeasurementIds = rawIds => {
  if (!rawIds) {
    return [];
  }

  const asArray = Array.isArray(rawIds) ? rawIds : [rawIds];

  return asArray
    .map(entry => {
      if (!entry) {
        return null;
      }

      if (typeof entry === "string") {
        return entry;
      }

      if (typeof entry === "object") {
        return entry._id || null;
      }

      return null;
    })
    .filter(Boolean);
};

const mergeUnique = (...idLists) => {
  const deduped = new Set();

  idLists
    .filter(Boolean)
    .flat()
    .forEach(id => {
      if (id) {
        deduped.add(id);
      }
    });

  return Array.from(deduped);
};

const buildValueMap = (ids, measurementLookup) => {
  if (!ids?.length) {
    return {};
  }

  return ids.reduce((acc, id) => {
    const measurement = measurementLookup[id];
    if (measurement?.unique_identifier !== undefined) {
      acc[measurement.unique_identifier] = measurement.value;
    }
    return acc;
  }, {});
};

/**
 * Fetch and shape measurements for Align without relying on MeasurementsContext.
 * - Pulls original measurements from DX instances.
 * - Pulls current/target from the active plan.
 * - Pulls current/target for the compared plan (read-only).
 * - Supports appending freshly created measurement IDs (from sync hook) so UI
 *   can render values before plans/instances are fully refreshed.
 */
const useAlignMeasurements = ({ instances, currentPlan, comparedPlan }) => {
  const instanceMeasurementIds = useMemo(() => {
    if (!instances || Object.keys(instances).length === 0) {
      return [];
    }

    const lateralIds = normalizeMeasurementIds(
      instances.sagittal_dx_instance_id?.measurements
    );
    const frontalIds = normalizeMeasurementIds(
      instances.coronal_dx_instance_id?.measurements
    );

    return mergeUnique(lateralIds, frontalIds);
  }, [instances]);

  const currentMeasurementIds = useMemo(
    () => normalizeMeasurementIds(currentPlan?.current_measurements),
    [currentPlan?.current_measurements]
  );

  const targetMeasurementIds = useMemo(
    () => normalizeMeasurementIds(currentPlan?.target_measurements),
    [currentPlan?.target_measurements]
  );

  const comparedCurrentMeasurementIds = useMemo(
    () => normalizeMeasurementIds(comparedPlan?.current_measurements),
    [comparedPlan?.current_measurements]
  );

  const comparedTargetMeasurementIds = useMemo(
    () => normalizeMeasurementIds(comparedPlan?.target_measurements),
    [comparedPlan?.target_measurements]
  );

  // Measurement IDs created by the sync hook before plans/instances are refreshed
  const [additionalMeasurementIds, setAdditionalMeasurementIds] = useState({
    original: [],
    current: [],
    target: [],
  });

  const allMeasurementIds = useMemo(
    () =>
      mergeUnique(
        instanceMeasurementIds,
        currentMeasurementIds,
        targetMeasurementIds,
        comparedCurrentMeasurementIds,
        comparedTargetMeasurementIds,
        additionalMeasurementIds.original,
        additionalMeasurementIds.current,
        additionalMeasurementIds.target
      ),
    [
      instanceMeasurementIds,
      currentMeasurementIds,
      targetMeasurementIds,
      comparedCurrentMeasurementIds,
      comparedTargetMeasurementIds,
      additionalMeasurementIds,
    ]
  );

  const {
    measurements,
    loading: measurementsLoading,
    error: measurementsError,
    refetch: refetchMeasurements,
  } = useMeasurementsQuery(allMeasurementIds);

  const measurementLookup = useMemo(() => {
    return measurements.reduce((acc, measurement) => {
      if (measurement?._id) {
        acc[measurement._id] = measurement;
      }
      return acc;
    }, {});
  }, [measurements]);

  const originalMeasurements = useMemo(
    () =>
      buildValueMap(
        mergeUnique(instanceMeasurementIds, additionalMeasurementIds.original),
        measurementLookup
      ),
    [instanceMeasurementIds, additionalMeasurementIds.original, measurementLookup]
  );

  const currentMeasurements = useMemo(
    () =>
      buildValueMap(
        mergeUnique(currentMeasurementIds, additionalMeasurementIds.current),
        measurementLookup
      ),
    [currentMeasurementIds, additionalMeasurementIds.current, measurementLookup]
  );

  const targetMeasurements = useMemo(
    () =>
      buildValueMap(
        mergeUnique(targetMeasurementIds, additionalMeasurementIds.target),
        measurementLookup
      ),
    [targetMeasurementIds, additionalMeasurementIds.target, measurementLookup]
  );

  const comparedCurrentMeasurements = useMemo(
    () => buildValueMap(comparedCurrentMeasurementIds, measurementLookup),
    [comparedCurrentMeasurementIds, measurementLookup]
  );

  const comparedTargetMeasurements = useMemo(
    () => buildValueMap(comparedTargetMeasurementIds, measurementLookup),
    [comparedTargetMeasurementIds, measurementLookup]
  );

  const registerSyncedMeasurements = useCallback(syncResult => {
    if (!syncResult) {
      return;
    }

    setAdditionalMeasurementIds(prev => ({
      original: mergeUnique(prev.original, syncResult.original?.createdIds),
      current: mergeUnique(prev.current, syncResult.current?.createdIds),
      target: mergeUnique(prev.target, syncResult.target?.createdIds),
    }));
  }, []);

  const hasAnyMeasurementIds = useMemo(() => {
    return (
      instanceMeasurementIds.length +
        currentMeasurementIds.length +
        targetMeasurementIds.length +
        comparedCurrentMeasurementIds.length +
        comparedTargetMeasurementIds.length +
        additionalMeasurementIds.original.length +
        additionalMeasurementIds.current.length +
        additionalMeasurementIds.target.length >
      0
    );
  }, [
    instanceMeasurementIds,
    currentMeasurementIds,
    targetMeasurementIds,
    comparedCurrentMeasurementIds,
    comparedTargetMeasurementIds,
    additionalMeasurementIds,
  ]);

  return {
    originalMeasurements,
    currentMeasurements,
    targetMeasurements,
    comparedCurrentMeasurements,
    comparedTargetMeasurements,
    isFetchingMeasurements: measurementsLoading && hasAnyMeasurementIds,
    isWaitingForMeasurements: !hasAnyMeasurementIds && !measurementsLoading,
    measurementError: measurementsError,
    refetchMeasurements,
    registerSyncedMeasurements,
  };
};

export default useAlignMeasurements;
